import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import {
  AddComment,
  AssignmentTurnedIn,
  CreditCard,
  DirectionsCar,
  EventAvailable,
  Handshake,
  EmojiEvents,
  Groups,
  Insights,
  Paid,
  Payments,
  PendingActions,
  Percent,
  PersonAddAlt1,
  PersonSearch,
  PriceCheck,
  ReportProblem,
  SwapHoriz,
  Timer,
  TrendingDown,
} from "@mui/icons-material";
import { colors, kpiLabels, kpiStatusStyles } from "../utils/datosTheme";
import { formatMoney, formatNumber } from "../utils/datosFormatters";

const kpiIcons = {
  clientes_nuevos: PersonAddAlt1,
  clientes_futuro: EventAvailable,
  clientes_convertidos: Handshake,
  clientes_baja: TrendingDown,
  consultas_nuevas: AddComment,
  consultas_promedio_por_cliente: AddComment,
  leads_sin_asignar: PersonSearch,
  consultas_activas: PendingActions,
  ventas_cerradas: Handshake,
  bajas: TrendingDown,
  futuros: EventAvailable,
  conversion_pct: Percent,
  monto_vendido: Paid,
  ticket_promedio: PriceCheck,
  tiempo_promedio_cierre_dias: Timer,
  mejor_vendedor: EmojiEvents,
  vendedores_activos: Groups,
  leads_asignados: AssignmentTurnedIn,
  leads_activos: PendingActions,
  ventas_financiadas: CreditCard,
  ventas_contado: Payments,
  ventas_con_usado: SwapHoriz,
  consultas_vehiculos: AddComment,
  modelos_consultados: DirectionsCar,
  vehiculos_vendidos: Handshake,
  modelos_vendidos: Paid,
  modelo_mas_consultado: PersonSearch,
  modelo_mas_vendido: DirectionsCar,
  precio_promedio_vendido: PriceCheck,
  estadio_mas_problematico: ReportProblem,
  bajas_estadio_problematico: TrendingDown,
  estancados_estadio_problematico: Timer,
  leads_estancados: PendingActions,
};

function KpiTitle({ id, description }) {
  const status = kpiStatusStyles[id];

  return (
    <Box sx={{ minWidth: 0 }}>
      {status ? (
        <Chip
          size="small"
          label={status.label}
          sx={{
            height: 24,
            color: status.text,
            bgcolor: status.bg,
            border: `1px solid ${status.solid}55`,
            fontWeight: 900,
            fontSize: 16,
          }}
        />
      ) : (
        <Typography sx={{ color: colors.ink, fontSize: 16, fontWeight: 850 }}>
          {kpiLabels[id]}
        </Typography>
      )}
      {description && (
        <Typography
          className="kpi-description"
          sx={{
            color: colors.muted,
            fontSize: 10,
            mt: 0,
            fontWeight: 600,
            lineHeight: 1.25,
            maxHeight: 0,
            opacity: 0,
            overflow: "hidden",
            transform: "translateY(-6px)",
            transition: "max-height 180ms ease, opacity 160ms ease, transform 180ms ease, margin-top 180ms ease",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

export default function KpiCard({ id, value, helper, currency, description }) {
  const Icon = kpiIcons[id] || Insights;
  const isPercent = id === "conversion_pct";
  const isMoney = id === "monto_vendido" || id === "ticket_promedio" || id === "precio_promedio_vendido";
  const isDuration = id === "tiempo_promedio_cierre_dias";
  const isAverage = id === "consultas_promedio_por_cliente";
  const isText = typeof value === "string";
  const display = isMoney
    ? formatMoney(value, currency)
    : isDuration
      ? `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })} días`
    : isAverage
      ? Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })
    : isPercent
      ? `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
      : isText
        ? value
      : formatNumber(value);

  return (
    <Box
      sx={{
        minHeight: 112,
        border: `1px solid ${colors.line}`,
        borderRadius: "8px",
        backgroundColor: colors.panel,
        boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
        px: 2,
        py: 1.8,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 0,
        "&:hover .kpi-description, &:focus-within .kpi-description": {
          maxHeight: 34,
          opacity: 1,
          mt: 0.65,
          transform: "translateY(0)",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, alignItems: "flex-start" }}>
        <KpiTitle id={id} description={description} />
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: "#eef5ff",
            color: colors.blue,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 19 }} />
        </Box>
      </Box>
      <Box>
        <Typography
          sx={{
            color: colors.ink,
            fontWeight: 950,
            fontSize: isMoney || isText || isDuration ? { xs: 22, md: 24 } : { xs: 25, md: 28 },
            lineHeight: 1.05,
            wordBreak: "break-word",
          }}
        >
          {display}
        </Typography>
        {helper && (
          <Typography sx={{ color: colors.muted, fontSize: 11.5, mt: 0.5, fontWeight: 700 }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
